import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { captureAttribution } from "@/lib/attribution";
import { initPixel, trackPageView } from "@/lib/fbPixel";

import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Onboarding from "@/pages/Onboarding";
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import GrantSearch from "@/pages/GrantSearch";
import GrantDetail from "@/pages/GrantDetail";
import CaGrantDetail from "@/pages/CaGrantDetail";
import ProposalEditor from "@/pages/ProposalEditor";
import Proposals from "@/pages/Proposals";
import Settings from "@/pages/Settings";
import Subscribe from "@/pages/Subscribe";
import Reporting from "@/pages/Reporting";
import AdminUsers from "@/pages/AdminUsers";
import AdminBankAccount from "@/pages/AdminBankAccount";
import Help from "@/pages/Help";
import Legal, { TermsOfService, PrivacyPolicy, CopyrightAndDmca } from "@/pages/Legal";
import Company from "@/pages/Company";
import Templates from "@/pages/Templates";
import TemplateDetail from "@/pages/TemplateDetail";
import AdminTemplates from "@/pages/AdminTemplates";
import SavedSearches from "@/pages/SavedSearches";
import AdminRevenue from "@/pages/AdminRevenue";
import AdminAttribution from "@/pages/AdminAttribution";

const Private = ({ children }) => (
  <ProtectedRoute>
    <AppLayout>{children}</AppLayout>
  </ProtectedRoute>
);

/**
 * Marketing-attribution + Facebook Pixel side-effects. Runs on mount and on
 * every route change: captures first-touch attribution and fires a PageView.
 */
function TrackingEffects() {
  const location = useLocation();
  useEffect(() => { initPixel(); }, []);
  useEffect(() => {
    captureAttribution();
    trackPageView();
  }, [location.pathname, location.search]);
  return null;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <TrackingEffects />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/" element={<Landing />} />
            <Route path="/legal" element={<Legal />} />
            <Route path="/legal/terms" element={<TermsOfService />} />
            <Route path="/legal/privacy" element={<PrivacyPolicy />} />
            <Route path="/legal/copyright" element={<CopyrightAndDmca />} />
            <Route path="/dashboard" element={<Private><Dashboard /></Private>} />
            <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
            <Route path="/search" element={<Private><GrantSearch /></Private>} />
            <Route path="/grants" element={<Navigate to="/search" replace />} />
            <Route path="/grants/ca/:id" element={<Private><CaGrantDetail /></Private>} />
            <Route path="/grants/:id" element={<Private><GrantDetail /></Private>} />
            <Route path="/proposals" element={<Private><Proposals /></Private>} />
            <Route path="/proposals/:id" element={<Private><ProposalEditor /></Private>} />
            <Route path="/reporting" element={<Private><Reporting /></Private>} />
            <Route path="/subscribe" element={<Private><Subscribe /></Private>} />
            <Route path="/help" element={<Private><Help /></Private>} />
            <Route path="/settings" element={<Private><Settings /></Private>} />
            <Route path="/admin/users" element={<Private><AdminUsers /></Private>} />
            <Route path="/admin/bank-account" element={<Private><AdminBankAccount /></Private>} />
            <Route path="/admin/templates" element={<Private><AdminTemplates /></Private>} />
            <Route path="/company" element={<Private><Company /></Private>} />
            <Route path="/templates" element={<Private><Templates /></Private>} />
            <Route path="/templates/:id" element={<Private><TemplateDetail /></Private>} />
            <Route path="/saved-searches" element={<Private><SavedSearches /></Private>} />
            <Route path="/admin/revenue" element={<Private><AdminRevenue /></Private>} />
            <Route path="/admin/attribution" element={<Private><AdminAttribution /></Private>} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
